import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { onPushMessage } from '@/lib/push/messages'
import { describeCallStatus, isTerminalCallStatus } from '@/lib/push/call-status'
import { initMessaging } from '@/lib/firebase/messaging'
import { queryKeys } from '@/hooks/queries/keys'
import type { Call, CallDetails, CallMessage, ProfileResponse } from '@/lib/api/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

/**
 * Session-wide push wiring: FCM setup (with a permission explainer) and
 * handlers for messages that matter on every screen.
 */
export function PushHandler() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [askPermission, setAskPermission] = useState(false)

  // Notification permission + FCM registration.
  useEffect(() => {
    if (!('Notification' in window)) return
    if (Notification.permission === 'granted') {
      void initMessaging().catch((error) => console.error('FCM init failed', error))
    } else if (Notification.permission === 'default') {
      setAskPermission(true)
    }
  }, [])

  const requestPermission = async () => {
    setAskPermission(false)
    const status = await Notification.requestPermission()
    if (status === 'granted') {
      await initMessaging().catch((error) => console.error('FCM init failed', error))
    } else {
      toast.warning('Notifications are needed to follow calls in the background')
    }
  }

  // Global push message handling.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  useEffect(() => {
    return onPushMessage((message) => {
      switch (message.action) {
        case 'call_message': {
          const sid = message.callSid
          if (!sid) break
          const entry: CallMessage = {
            id: message.id,
            text: message.text ?? '',
            sent: message.sent,
            time: Date.now(),
          }
          const appendMessage = <T extends { messages?: CallMessage[] }>(record: T): T => {
            const messages = [...(record.messages ?? [])]
            const index = message.id ? messages.findIndex((m) => m.id === message.id) : -1
            if (index >= 0) {
              // Partial utterances stream in under the same id; replace in place.
              messages.splice(index, 1)
            }
            messages.push(entry)
            return { ...record, messages }
          }
          // Live view reads the call-details cache; seed it if the push
          // arrives before the fetch resolves so no lines are lost.
          queryClient.setQueryData<CallDetails>(queryKeys.callDetails(sid), (details) =>
            appendMessage(details ?? { callSid: sid, state: 'in-progress' }),
          )
          // Ended-call cache (if present) gets the same append.
          queryClient.setQueryData<Call>(queryKeys.call(sid), (call) =>
            call ? appendMessage(call) : call,
          )
          break
        }
        case 'call_status': {
          // `id` is the callSid here. `rejected` (and friends) may never be
          // followed by a hangup push, so refresh everything that shows state.
          const sid = message.id
          void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
          if (sid) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.call(sid) })
          }
          if (isTerminalCallStatus(message.status)) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.profile })
            if (message.status && message.status !== 'completed') {
              toast.warning(describeCallStatus(message.status))
            }
          }
          break
        }
        case 'hangup':
        case 'media_stop': {
          void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
          void queryClient.invalidateQueries({ queryKey: queryKeys.profile })
          if (message.callSid) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.call(message.callSid) })
          }
          break
        }
        case 'media_start': {
          if (message.callSid) {
            void navigateRef.current(`/live/${message.callSid}`)
          }
          break
        }
        case 'call':
        case 'recording': {
          // Transcript/summary or recording became available after the call.
          void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
          if (message.callSid) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.call(message.callSid) })
          }
          break
        }
        case 'scheduled_call_status': {
          void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
          break
        }
        case 'subscription': {
          setTimeout(() => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.subscription })
            toast.success('Subscription updated')
          }, 2000)
          break
        }
        case 'billing': {
          void queryClient.invalidateQueries({ queryKey: queryKeys.profile })
          void queryClient.invalidateQueries({ queryKey: queryKeys.subscription })
          break
        }
        case 'credits': {
          void queryClient.invalidateQueries({ queryKey: queryKeys.profile })
          break
        }
        case 'not_enough_credits': {
          toast.warning("You don't have enough credits to make this call")
          break
        }
        case 'number_verification': {
          void queryClient.invalidateQueries({ queryKey: queryKeys.profile })
          toast.success('Your number is verified')
          break
        }
        case 'number_verification_error': {
          toast.error('Number verification failed')
          break
        }
        case 'contacts':
        case 'calendar': {
          // Unlock the UI immediately, then refetch the real profile record.
          const field = message.action
          queryClient.setQueryData<ProfileResponse>(queryKeys.profile, (profile) =>
            profile ? { ...profile, user: { ...profile.user, [field]: true } } : profile,
          )
          void queryClient.invalidateQueries({ queryKey: queryKeys.profile })
          if (field === 'contacts') {
            void queryClient.invalidateQueries({ queryKey: queryKeys.contacts })
            toast.success('Google contacts connected')
          } else {
            toast.success('Google calendar connected')
          }
          break
        }
        case 'wakeup':
          // Mobile-app wake ping; nothing to do on the web.
          break
      }
    })
  }, [queryClient])

  return (
    <Dialog open={askPermission} onOpenChange={setAskPermission}>
      <DialogContent className="sm:max-w-sm" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Turn on notifications</DialogTitle>
          <DialogDescription>
            VOYP uses notifications to keep you posted while a call is running — live transcript
            lines, copilot questions, and the result when it ends.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setAskPermission(false)}>
            Not now
          </Button>
          <Button onClick={() => void requestPermission()}>Allow notifications</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
